// ── LedgerTab.js — 거래장부 탭 (TaxCenter에 통합) ─────────────────────────
// 거래 목록 + 추가/수정/삭제 + 기간 요약

import React, { useState, useMemo } from 'react';
import {
  CATEGORY_MAJOR, EVIDENCE_TYPES,
  categoryLabel, evidenceLabel,
  pickPresumedRate, calcPresumedDeduction,
} from '../data/taxConstants';
import { useTransactions, usePartners, summarizeTransactions, filterByPeriod } from '../useTransactions';
import { useTaxPeriod, periodLabel as periodLabelFn, estimateAnnualRevenue } from '../useTaxPeriod';
import TransactionEditor from './TransactionEditor';
import PrintReport from './PrintReport';
import VATReturnHelper from './VATReturnHelper';
import PresumedDeductionCard from './PresumedDeductionCard';
import MonthlyRevenueInput from './MonthlyRevenueInput';
import BatchReceiptUpload from './BatchReceiptUpload';
import { usePlan } from '../usePlan';
import { isOverTransactionLimit, FREE_LIMITS, countMonthlyTransactions, txnLimitMessage } from '../utils/freeLimits';
import { exportTaxXlsx } from '../utils/taxExport';
import { saveItemPrices } from '../utils/itemPrices';
import { exportSingleSheet, buildTransactionsSheet } from '../utils/backupExport';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;
const thisQuarter = Math.ceil(thisMonth / 3);

export default function LedgerTab({ user, prices = [], profitLoss = {} }) {
  const [year, setYear] = useState(thisYear);
  const [quarter, setQuarter] = useState(null);  // null = 전체 분기 (자동입력 거래 발견성 향상)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [defaultType, setDefaultType] = useState('expense');
  const [filter, setFilter] = useState('all'); // 'all' | 'income' | 'expense'
  const [printOpen, setPrintOpen] = useState(false);
  const [vatHelperOpen, setVatHelperOpen] = useState(false);
  const [revenueInputOpen, setRevenueInputOpen] = useState(false);
  const [batchOcrOpen, setBatchOcrOpen] = useState(false);

  const { transactions, loading, addTransaction, updateTransaction, deleteTransaction } = useTransactions(user, { limitCount: 5000 });
  const { partners, addPartner } = usePartners(user);
  const { plan } = usePlan(user);
  const { period: taxPeriod, setMonthlyRevenue } = useTaxPeriod(user, year, quarter || 1);

  // 기간 + 필터 적용
  const filtered = useMemo(() => {
    let list = filterByPeriod(transactions, year, quarter);
    if (filter !== 'all') list = list.filter(t => t.type === filter);
    return list;
  }, [transactions, year, quarter, filter]);

  // 요약
  const summary = useMemo(() => summarizeTransactions(filtered), [filtered]);

  const handleSaveTxn = async (txn) => {
    if (txn.id) {
      const { id, ...rest } = txn;
      await updateTransaction(id, rest);
    } else {
      // V29: 무료 플랜 거래장부 월 100건 한도
      if (isOverTransactionLimit(transactions, plan, 1)) {
        const used = countMonthlyTransactions(transactions);
        alert('이번 달 거래 입력 한도(' + FREE_LIMITS.transactionsPerMonth + '건)를 초과했어요.\n' +
          '사용: ' + used + '/' + FREE_LIMITS.transactionsPerMonth + '건\n\n' +
          '평생 Pro 이용권(1회 결제)으로 무제한 사용 가능합니다.');
        return;
      }
      await addTransaction(txn);
    }
  };

  const handleSavePartner = async (partner) => {
    return await addPartner(partner);
  };

  const openNew = (type) => {
    setEditingTxn(null);
    setDefaultType(type);
    setEditorOpen(true);
  };

  const openEdit = (txn) => {
    setEditingTxn(txn);
    setEditorOpen(true);
  };

  const partnerName = (partnerId, partnerNameFallback) => {
    if (partnerNameFallback) return partnerNameFallback;
    const p = partners.find(x => x.id === partnerId);
    return p ? p.name : '-';
  };

  // ── 기간 라벨 ──
  const periodLabel = useMemo(() => {
    if (!quarter) return `${year}년 전체`;
    return `${year}년 ${quarter}분기`;
  }, [year, quarter]);

  // ── 의제매입공제 정보 ──
  const presumedTransactions = useMemo(
    () => filtered.filter(t => t.type === 'expense' && t.vatTreatment?.presumed),
    [filtered]
  );
  const presumedSupply = useMemo(
    () => presumedTransactions.reduce((s, t) => s + (Number(t.supply) || 0), 0),
    [presumedTransactions]
  );
  const annualRevenueEst = useMemo(() => {
    if (!taxPeriod) return null;
    return estimateAnnualRevenue({
      monthlyRevenue: taxPeriod.monthlyRevenue,
      annualRevenue: taxPeriod.annualRevenue,
      year, quarter: quarter || 1,
    });
  }, [taxPeriod, year, quarter]);

  // ── 출력 핸들러 ──
  const handleExportXlsx = async () => {   // V148 엑셀 라이브러리를 그때 받는다
    if (filtered.length === 0) {
      alert('출력할 거래가 없습니다. 거래를 먼저 추가해주세요.');
      return;
    }
    try {
      await exportTaxXlsx({
        transactions: filtered,
        partners,
        user,
        periodLabel,
        annualRevenue: annualRevenueEst,
        industry: 'food',
      });
    } catch (e) {
      console.error(e);
      alert('엑셀 생성 실패: ' + (e.message || e));
    }
  };

  // ── 스타일
  const card = {
    background: '#fff', borderRadius: 10, padding: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── 의제매입공제 환급 추정 카드 (V1.5 wedge) ── */}
      <PresumedDeductionCard
        presumedSupply={presumedSupply}
        annualRevenue={annualRevenueEst}
        industry="food"
        presumedTransactions={presumedTransactions}
        periodLabel={periodLabel}
        onOpenRevenueInput={() => setRevenueInputOpen(true)}
      />

      {/* ── 기간 선택 + 추가 버튼 ── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 7, fontSize: 13 }}>
          {[thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select value={quarter || 0} onChange={e => setQuarter(parseInt(e.target.value, 10) || null)}
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 7, fontSize: 13 }}>
          <option value={0}>전체</option>
          <option value={1}>1분기 (1~3월)</option>
          <option value={2}>2분기 (4~6월)</option>
          <option value={3}>3분기 (7~9월)</option>
          <option value={4}>4분기 (10~12월)</option>
        </select>
        {['all', 'income', 'expense'].map(f => (
          <button key={f} onClick={() => setFilter(f)} type="button"
            style={{
              padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
              border: '1px solid', borderColor: filter === f ? '#FF6F0F' : '#e5e7eb',
              background: filter === f ? '#FF6F0F' : '#fff',
              color: filter === f ? '#fff' : '#666', cursor: 'pointer',
            }}>
            {f === 'all' ? '전체' : f === 'income' ? '매출' : '매입'}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => setRevenueInputOpen(true)} type="button"
          title="이 분기 월별 매출 입력 (의제매입공제 한도 계산용)"
          style={{
            padding: '6px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600,
            border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
          }}>
          💰 매출 입력
        </button>
        {/* 📥 거래장부 엑셀 다운로드 */}
        <button onClick={() => {
          try {
            const aoa = buildTransactionsSheet(transactions || []);
            const d = new Date();
            const fileName = `거래장부_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}.xlsx`;
            exportSingleSheet({ fileName, sheetName: '거래장부', aoa });
          } catch (e) { alert('다운로드 실패: ' + e.message); }
        }} type="button"
          title="이 거래장부를 엑셀로 저장"
          style={{
            padding: '7px 12px', borderRadius: 7, border: '1px solid #FF6F0F',
            background: '#fff', color: '#18181B', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>
          📥 엑셀
        </button>
        {/* 📸 영수증 OCR — 카메라/갤러리 통합 */}
        <button onClick={() => setBatchOcrOpen(true)} type="button"
          title="영수증 사진으로 거래 자동 입력 (카메라·갤러리 다중 선택)"
          style={{
            position: 'relative',
            padding: '7px 14px', borderRadius: 7, border: '1px solid #FF6F0F',
            background: '#FF6F0F', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
          📸 영수증
          <span style={{
            marginLeft: 5, fontSize: 9, fontWeight: 800,
            padding: '2px 6px', borderRadius: 8,
            background: '#F4F4F5', color: '#52525B', verticalAlign: 'middle',
          }}>Beta</span>
        </button>
        <button onClick={() => openNew('expense')} type="button"
          style={{
            padding: '7px 14px', borderRadius: 7, border: '1px solid #71717A',
            background: '#fff', color: '#71717A', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>+ 매입</button>
        <button onClick={() => openNew('income')} type="button"
          style={{
            padding: '7px 14px', borderRadius: 7, border: 'none',
            background: '#FF6F0F', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>+ 매출</button>
      </div>

      {/* ── 요약 카드 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#888' }}>매출</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#18181B' }}>{fmt(summary.income)}원</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#888' }}>매입</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#71717A' }}>{fmt(summary.expense)}원</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#888' }}>이익</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: summary.profit >= 0 ? '#FF6F0F' : '#71717A' }}>
            {fmt(summary.profit)}원
          </div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#888' }}>적격증빙 비율</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>
            {Math.round(summary.qualifiedRatio * 100)}%
          </div>
          {summary.unqualifiedExpense > 0 && (
            <div style={{ fontSize: 10, color: '#EA580C', marginTop: 2 }}>
              비적격 {fmt(summary.unqualifiedExpense)}원
            </div>
          )}
        </div>
      </div>

      {/* ── 카테고리별 분포 (간단) ── */}
      {Object.keys(summary.byMajor).length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: '#555' }}>매입 카테고리별</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {CATEGORY_MAJOR.map(m => {
              const v = summary.byMajor[m.id] || 0;
              if (v <= 0) return null;
              const pct = summary.expense > 0 ? Math.round((v / summary.expense) * 100) : 0;
              return (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', background: '#fff', borderRadius: 7,
                  border: '1px solid ' + m.color + '33',
                }}>
                  <span>{m.emoji}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{m.label}</span>
                  <span style={{ fontSize: 11, color: '#888' }}>{fmt(v)}원 ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 거래 목록 ── */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>
            거래 내역 {filtered.length > 0 && <span style={{ color: '#888', fontWeight: 400 }}>({filtered.length}건)</span>}
          </div>
        </div>

        {loading && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>불러오는 중...</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ fontSize: 13, color: '#999', padding: '40px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📒</div>
            <div>아직 입력한 거래가 없습니다</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>매출/매입 한 건 추가해서 시작해보세요</div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {filtered.map((t, i) => {
              const isIncome = t.type === 'income';
              const cat = t.category || {};
              const evid = t.evidence || {};
              const isPersonal = t.incomeTreatment?.isPersonal;
              return (
                <div key={t.id} onClick={() => openEdit(t)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 6px', cursor: 'pointer',
                    borderBottom: i < filtered.length - 1 ? '1px solid #f5f5f5' : 'none',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  {/* 일자 */}
                  <div style={{ fontSize: 11, color: '#888', flex: '0 0 70px' }}>
                    {t.date ? t.date.slice(5) : '-'}
                  </div>
                  {/* 종류 표시 */}
                  <div style={{ flex: '0 0 18px', textAlign: 'center', fontSize: 14 }}>
                    {isIncome ? '↑' : '↓'}
                  </div>
                  {/* 거래처 + 품목 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {partnerName(t.partnerId, t.partnerName) || (isIncome ? '매출' : '매입')}
                      {isPersonal && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>🏠 개인</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isIncome
                        ? (t.revenueCategory || '매출')
                        : categoryLabel(cat.major, cat.sub)}
                      {t.itemName && ' · ' + t.itemName}
                    </div>
                  </div>
                  {/* 증빙 / 의제매입 */}
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                    {evid.type && (
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: evid.isQualified ? '#FAFAFA' : '#F4F4F5',
                        color: evid.isQualified ? '#FF6F0F' : '#EA580C',
                      }}>
                        {evidenceLabel(evid.type)}
                      </span>
                    )}
                    {t.vatTreatment?.presumed && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: "#FAFAFA", color: '#18181B' }}>
                        의제
                      </span>
                    )}
                  </div>
                  {/* 금액 */}
                  <div style={{
                    flex: '0 0 90px', textAlign: 'right', fontWeight: 700, fontSize: 14,
                    color: isIncome ? '#FF6F0F' : '#71717A',
                  }}>
                    {isIncome ? '+' : '-'}{fmt(t.total)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 출력 영역 ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>📤 출력</span>
          <span style={{ fontSize: 11, color: '#888' }}>{periodLabel} · {filtered.length}건 기준</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <ExportButton
            icon="📊"
            title="운영 보고서 (PDF)"
            desc="자영업자용 한 페이지. 인쇄 또는 PDF 저장"
            color="#FF6F0F"
            onClick={() => setPrintOpen(true)}
            disabled={filtered.length === 0}
          />
          <ExportButton
            icon="📋"
            title="세무사용 엑셀"
            desc="매출·매입·요약·메타 4시트 xlsx"
            color="#3b82f6"
            onClick={handleExportXlsx}
            disabled={filtered.length === 0}
          />
          <ExportButton
            icon="📤"
            title="부가세 신고서 미리채움"
            desc="홈택스 신고서 칸별 합계 + 입력 가이드"
            color="#FF6F0F"
            onClick={() => setVatHelperOpen(true)}
            disabled={filtered.length === 0}
          />
        </div>
      </div>

      {/* ── 모달 ── */}
      {editorOpen && (
        <TransactionEditor
          initial={editingTxn}
          defaultType={defaultType}
          partners={partners}
          onSave={handleSaveTxn}
          onSavePartner={handleSavePartner}
          onDelete={deleteTransaction}
          onClose={() => { setEditorOpen(false); setEditingTxn(null); }}
        />
      )}
      {printOpen && (
        <PrintReport
          transactions={filtered}
          partners={partners}
          periodLabel={periodLabel}
          annualRevenue={annualRevenueEst}
          industry="food"
          onClose={() => setPrintOpen(false)}
        />
      )}
      {vatHelperOpen && (
        <VATReturnHelper
          transactions={filtered}
          periodLabel={periodLabel}
          annualRevenue={annualRevenueEst}
          onClose={() => setVatHelperOpen(false)}
        />
      )}
      {revenueInputOpen && (
        <MonthlyRevenueInput
          year={year}
          quarter={quarter || 1}
          monthlyRevenue={taxPeriod?.monthlyRevenue || {}}
          profitLoss={profitLoss}
          onSave={async (mr) => { await setMonthlyRevenue(mr); }}
          onClose={() => setRevenueInputOpen(false)}
        />
      )}
      {batchOcrOpen && (
        <BatchReceiptUpload
          user={user}
          defaultType="expense"
          prices={prices}
          onSaveAll={async (txns, cardLineItemsList) => {
            // 1) 거래 일괄 저장 (sourceReceiptId 객체에 박혀있음)
            for (const t of txns) {
              await addTransaction(t);
            }
            // 2) 가격 인텔리전스 — lineItems를 itemPrices에 누적 (단가표 매칭)
            try {
              for (const entry of (cardLineItemsList || [])) {
                if (!entry?.lineItems || entry.lineItems.length === 0) continue;
                await saveItemPrices(user.uid, entry.lineItems, prices, {
                  date: entry.card?.ocr?.date,
                  partnerName: entry.card?.ocr?.partnerName,
                  source: 'ocr',
                });
              }
            } catch (e) {
              console.warn('itemPrices save failed:', e);
            }
          }}
          onClose={() => setBatchOcrOpen(false)}
        />
      )}
    </div>
  );
}

function ExportButton({ icon, title, desc, color, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} type="button"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
        padding: 14, borderRadius: 10,
        border: '1.5px solid', borderColor: disabled ? '#e5e7eb' : color + '55',
        background: disabled ? '#f9fafb' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'all .15s',
      }}>
      <div style={{ fontSize: 22, lineHeight: 1 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: disabled ? '#aaa' : color }}>{title}</div>
      <div style={{ fontSize: 11, color: disabled ? '#aaa' : '#666' }}>{desc}</div>
    </button>
  );
}
